import { Injectable } from '@angular/core'
import { Actions, Effect } from '@ngrx/effects'
import { Action } from '@ngrx/store'
import { Observable } from 'rxjs/Observable'
import { ContactService, ApplyDiscountParams, RemoveDiscountParams, ContactSearchParams } from "@services/contact"
import { EnrolmentService, FetchEnrolmentsParams } from "@services/enrolment";
import { Contact } from "@models/contact"
import { Absence } from "@models/absence"
import { ClassBetween } from "@models/class-between"
import {
  FETCH_CONTACTS,
  POPULATE_CONTACTS,
  FETCH_CONTACTS_FAILURE,
  CREATE_CONTACT,
  CREATE_CONTACT_FAILURE,
  ADD_CONTACT,
  FETCH_CONTACT,
  FETCH_CONTACT_FAILURE,
  POPULATE_ACTIVE_CONTACT,
  UPDATE_CONTACT,
  UPDATE_CONTACT_FAILURE,
  REPLACE_CONTACT,
  FETCH_ABSENCE_DATE_TIME_CHECK_CONTACT,
  UPDATE_ABSENCE_DATE_TIME_CHECK_CONTACT,
  CREATE_ABSENCE,
  DELETE_ABSENCE,
  FETCH_STUDENTS,
  POPULATE_STUDENTS,
  FETCH_STUDENTS_FAILURE,
  CREATE_DOCUMENT,
  CREATED_DOCUMENT,
  CREATE_DOCUMENT_FAILURE,
  DELETE_DOCUMENT,
  DELETED_DOCUMENT,
  DELETE_DOCUMENT_FAILURE,
  CREATED_NOTE,
  CREATE_NOTE,
  CREATE_NOTE_FAILURE,
  DELETE_NOTE,
  DELETED_NOTE,
  DELETE_NOTE_FAILURE,
  DELETE_ADDRESS,
  DELETED_ADDRESS,
  DELETE_ADDRESS_FAILURE,
  DELETE_PHONE,
  DELETED_PHONE,
  DELETE_PHONE_FAILURE,
  FETCH_ACTIVE_CONTACT_ENROLMENTS,
  FETCH_ACTIVE_CONTACT_ENROLMENTS_FAILURE,
  POPULATE_ACTIVE_CONTACT_ENROLMENTS,
  PERFORM_ACADEMIC_ROLLOVER,
  ACADEMIC_ROLLOVER_FAILURE,
  PERFORMED_ACADEMIC_ROLLOVER,
  APPLY_DISCOUNT_FAILURE,
  APPLIED_DISCOUNT,
  APPLY_DISCOUNT,
  REMOVE_DISCOUNT,
  REMOVE_DISCOUNT_FAILURE,
  REMOVE_DISCOUNT_SUCCESS
} from "../contacts"
import { Document } from "@models/document";
import { Note } from "@models/note";
import { Enrolment } from "@models/enrolment";

@Injectable()
export class ContactEffects {

  constructor(
    private actions: Actions,
    private contactService: ContactService,
    private enrolmentService: EnrolmentService,
  ) {}

  @Effect() fetchContacts = this.actions
    .ofType(FETCH_CONTACTS)
    .map((action: Action) => action.payload)
    .switchMap((params: ContactSearchParams) => this.contactService.index(params)
      .map((contacts: Contact[]) => ({ type: POPULATE_CONTACTS, payload: contacts }))
      .catch(() => Observable.of({ type: FETCH_CONTACTS_FAILURE }))
    )
    .share()

  @Effect() fetchContact: Observable<Action> = this.actions
    .ofType(FETCH_CONTACT)
    .map((action: Action) => action.payload)
    .switchMap((payload: number) => this.contactService.fetch(payload)
      .map((contact: Contact) => ({ type: POPULATE_ACTIVE_CONTACT, payload: contact }))
      .catch((response) => Observable.of({ type: FETCH_CONTACT_FAILURE, payload: response }))
    )
    .share()

  @Effect() fetchActiveContactEnrolments = this.actions
    .ofType(FETCH_ACTIVE_CONTACT_ENROLMENTS)
    .throttleTime(200)
    .map((action: Action) => action.payload)
    .switchMap((params: FetchEnrolmentsParams) => this.enrolmentService.fetchEnrolments(params)
      .map((es: Enrolment[]) => ({ type: POPULATE_ACTIVE_CONTACT_ENROLMENTS, payload: es }))
      .catch(() => Observable.of({ type: FETCH_ACTIVE_CONTACT_ENROLMENTS_FAILURE }))
    )
    .share()

  @Effect() createContact = this.actions
    .ofType(CREATE_CONTACT)
    .map((action: Action) => action.payload)
    .switchMap((payload: Contact) => this.contactService.create(payload.serializeForRails())
      .map(contact => ({ type: ADD_CONTACT, payload: contact }))
      .catch((response) => Observable.of({ type: CREATE_CONTACT_FAILURE, payload: response }))
    )
    .share()

  @Effect() updateContact = this.actions
    .ofType(UPDATE_CONTACT)
    .map((action: Action) => action.payload)
    .switchMap((payload: Contact) => this.contactService.update(payload.serializeForRails())
      .map(contact => ({ type: REPLACE_CONTACT, payload: contact }))
      .catch((response) => Observable.of({ type: UPDATE_CONTACT_FAILURE, payload: response }))
    )
    .share()

  @Effect() classesBetween = this.actions
    .ofType(FETCH_ABSENCE_DATE_TIME_CHECK_CONTACT)
    .map((action: Action) => action.payload)
    .switchMap((payload: any) => this.contactService.classesBetween(payload)
      .map((classesBetween: ClassBetween[]) => {
        return { type: UPDATE_ABSENCE_DATE_TIME_CHECK_CONTACT, payload: classesBetween }
      })
    )
    .share()

  @Effect() createAbsence = this.actions
    .ofType(CREATE_ABSENCE)
    .map((action: Action) => action.payload)
    .switchMap((payload: Absence) => this.contactService.createAbsence(payload)
      .map((a: Absence) => ({ type: FETCH_CONTACT, payload: a.contactId }))
    )
    .share()

  @Effect() deleteAbsence = this.actions
    .ofType(DELETE_ABSENCE)
    .map((action: Action) => action.payload)
    .switchMap((payload: Absence) => this.contactService.deleteAbsence(payload)
      .map((contactId) => ({ type: FETCH_CONTACT, payload: contactId }))
    )
    .share()

  @Effect() fetchStudents = this.actions
    .ofType(FETCH_STUDENTS)
    .switchMap(() => this.contactService.students()
      .map((students: Contact[]) => ({ type: POPULATE_STUDENTS, payload: students }))
      .catch(() => Observable.of({ type: FETCH_STUDENTS_FAILURE }))
    )
    .share()

  @Effect() createDocument = this.actions
    .ofType(CREATE_DOCUMENT)
    .map((action: Action) => action.payload)
    .switchMap((doc: Document) => this.contactService.createDocument(doc.serializeForRails())
      .map((doc: Document) => ({ type: CREATED_DOCUMENT, payload: doc }))
      .catch(() => Observable.of({ type: CREATE_DOCUMENT_FAILURE }))
    )
    .share()

  @Effect() deleteDocument = this.actions
    .ofType(DELETE_DOCUMENT)
    .map((action: Action) => action.payload)
    .switchMap((id: number) => this.contactService.deleteDocument(id)
      .map((id: number) => ({ type: DELETED_DOCUMENT, payload: id }))
      .catch(() => Observable.of({ type: DELETE_DOCUMENT_FAILURE }))
    )
    .share()

  @Effect() createNote = this.actions
    .ofType(CREATE_NOTE)
    .map((action: Action) => action.payload)
    .switchMap((note: Note) => this.contactService.createNote(note.serializeForRails())
      .map((note: Note) => ({ type: CREATED_NOTE, payload: note }))
      .catch(() => Observable.of({ type: CREATE_NOTE_FAILURE }))
    )
    .share()

  @Effect() deleteNote = this.actions
    .ofType(DELETE_NOTE)
    .map((action: Action) => action.payload)
    .switchMap((id: number) => this.contactService.deleteNote(id)
      .map((id: number) => ({ type: DELETED_NOTE, payload: id }))
      .catch(() => Observable.of({ type: DELETE_NOTE_FAILURE }))
    )
    .share()

  @Effect() deleteAddress = this.actions
    .ofType(DELETE_ADDRESS)
    .map((action: Action) => action.payload)
    .switchMap((id: number) => this.contactService.deleteAddress(id)
      .map((id: number) => ({ type: DELETED_ADDRESS, payload: id }))
      .catch(() => Observable.of({ type: DELETE_ADDRESS_FAILURE }))
    )
    .share()

  @Effect() deletePhone = this.actions
    .ofType(DELETE_PHONE)
    .map((action: Action) => action.payload)
    .switchMap((id: number) => this.contactService.deletePhone(id)
      .map((id: number) => ({ type: DELETED_PHONE, payload: id }))
      .catch(() => Observable.of({ type: DELETE_PHONE_FAILURE }))
    )
    .share()

  @Effect() academicRollover = this.actions
    .ofType(PERFORM_ACADEMIC_ROLLOVER)
    .switchMap(() => this.contactService.academicRollover()
      .map(() => ({ type: PERFORMED_ACADEMIC_ROLLOVER }))
      .catch(() => Observable.of({ type: ACADEMIC_ROLLOVER_FAILURE }))
    )

  @Effect() applyDiscount = this.actions
    .ofType(APPLY_DISCOUNT)
    .map((action: Action) => action.payload)
    .switchMap((params: ApplyDiscountParams) => this.contactService.applyDiscount(params)
      .map(() => ({ type: APPLIED_DISCOUNT }))
      .catch(() => Observable.of({ type: APPLY_DISCOUNT_FAILURE }))
    )

  @Effect() removeDiscount = this.actions
    .ofType(REMOVE_DISCOUNT)
    .map((action: Action) => action.payload)
    .switchMap((params: RemoveDiscountParams) => this.contactService.removeDiscount(params)
      .map(() => ({ type: REMOVE_DISCOUNT_SUCCESS }))
      .catch(() => Observable.of({ type: REMOVE_DISCOUNT_FAILURE }))
    )

}



// WEBPACK FOOTER //
// /Users/jason/src/matrix/client/src/app/reducers/effects/contact.ts